import {RouterModule} from '@angular/router';
import {CommonModule} from '@angular/common';
import {Component, DestroyRef, Inject, OnInit, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {InfiniteScrollModule} from 'ngx-infinite-scroll';
import {DashboardStatisticsService} from '../../../dashboard-analytics/services/dashboard-statistics.service';
import {EcuBuildStats} from '../../../dashboard-analytics/models/dashboard-statistics.model';

const LOAD_SIZE = 10;

@Component({
  selector: 'app-top-ecus-list-dialog',
  standalone: true,
  imports: [CommonModule, InfiniteScrollModule, RouterModule],
  templateUrl: './top-ecus-list-dialog.component.html',
  styleUrls: ['./top-ecus-list-dialog.component.css']
})
export class TopEcusListDialogComponent implements OnInit {
  private readonly stats = inject(DashboardStatisticsService);
  private readonly destroyRef = inject(DestroyRef);
  readonly dialogRef = inject(MatDialogRef<TopEcusListDialogComponent>);
  readonly ecus = signal<EcuBuildStats[]>([]);
  readonly isLoading = signal(false);
  readonly hasMore = signal(false);
  readonly error = signal('');
  private loadedLimit = 0;
  private requestedLimit = LOAD_SIZE;

  readonly title = this.data.type === 'missing' ? 'Missing ECUs' : 'Partial ECUs';
  readonly rows = computed(() => {
    const sorted = [...this.ecus()].sort((a, b) => b.count - a.count);
    const max = Math.max(1, sorted[0]?.count ?? 0);
    return sorted.map(row => ({...row, barPct: Math.round(row.count / max * 100)}));
  });

  constructor(@Inject(MAT_DIALOG_DATA) readonly data: {type: 'missing' | 'partial'}) {}

  ngOnInit(): void { this.fetch(LOAD_SIZE); }

  onScroll(): void {
    if (!this.isLoading() && this.hasMore() && !this.error()) this.fetch(this.loadedLimit + LOAD_SIZE);
  }

  retry(): void { this.fetch(this.requestedLimit); }

  private fetch(limit: number): void {
    this.isLoading.set(true);
    this.error.set('');
    this.requestedLimit = limit;
    // These endpoints provide a top-N limit rather than an offset. Increase it
    // by ten, keeping the existing rows visible while requesting the next batch.
    const request = this.data.type === 'missing'
      ? this.stats.getMissingMatchingByEcuBuild(limit)
      : this.stats.getPartialMatchingByEcuBuild(limit);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => {
        if (!Array.isArray(response)) {
          this.error.set('Could not load ECU builds. Please try again.');
        } else {
          this.ecus.set(response.slice(0, limit));
          this.loadedLimit = limit;
          this.hasMore.set(response.length >= limit);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.error.set('Could not load ECU builds. Please try again.');
        this.isLoading.set(false);
      }
    });
  }
}
